/**
 * Advanced Signals Panel - Phase 1, 2 & 3 Strategies
 * 
 * SESSION 95-96: Displays advanced trading signals from the backend:
 * 
 * Phase 1:
 * - Ledge Stop Loss Hunt (87% accuracy)
 * - Inventory Tracker (Too Short/Too Long)
 * - Absorption/Exhaustion Detection
 * - POC Position Bias
 * 
 * Phase 2:
 * - EP Zone Strategy
 * - DHOL Patterns
 * - Slingshot POC
 * - Auction Failure
 * 
 * Phase 3 (Context & Classification):
 * - Day Type Classifier
 * - Profile Shape Detector
 * - Open Auction Type Classifier
 * 
 * @author Senior HFT Architect
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { 
  Target, TrendingUp, TrendingDown, AlertTriangle, 
  Activity, BarChart2, RefreshCw, ChevronRight
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Signal type to display config
const SIGNAL_CONFIG = {
  // Phase 1 Signals
  LEDGE_SL_HUNT_LONG: {
    icon: TrendingUp,
    color: 'bg-emerald-500',
    textColor: 'text-emerald-400',
    label: 'Ledge Hunt LONG',
    accuracy: '87%',
    boost: '+15',
  },
  LEDGE_SL_HUNT_SHORT: {
    icon: TrendingDown,
    color: 'bg-red-500',
    textColor: 'text-red-400',
    label: 'Ledge Hunt SHORT',
    accuracy: '87%',
    boost: '+15',
  },
  INVENTORY_TOO_SHORT: {
    icon: AlertTriangle,
    color: 'bg-amber-500',
    textColor: 'text-amber-400',
    label: 'TOO SHORT',
    accuracy: '70-95%',
    boost: '+10-20',
  },
  INVENTORY_TOO_LONG: {
    icon: AlertTriangle,
    color: 'bg-purple-500',
    textColor: 'text-purple-400',
    label: 'TOO LONG',
    accuracy: '70-95%',
    boost: '+10-20',
  },
  EXHAUSTION_BULLISH: {
    icon: Activity,
    color: 'bg-green-500',
    textColor: 'text-green-400',
    label: 'Seller Exhaustion',
    accuracy: '60-75%',
    boost: '+8',
  },
  EXHAUSTION_BEARISH: {
    icon: Activity,
    color: 'bg-rose-500',
    textColor: 'text-rose-400',
    label: 'Buyer Exhaustion',
    accuracy: '60-75%',
    boost: '+8',
  },
  POC_BULLISH_BIAS: {
    icon: BarChart2,
    color: 'bg-teal-500',
    textColor: 'text-teal-400',
    label: 'POC Bullish',
    accuracy: '55-85%',
    boost: '+5',
  },
  POC_BEARISH_BIAS: {
    icon: BarChart2,
    color: 'bg-orange-500',
    textColor: 'text-orange-400',
    label: 'POC Bearish',
    accuracy: '55-85%',
    boost: '+5',
  },
  // Phase 2 Signals
  EP_ZONE_LONG: {
    icon: Target,
    color: 'bg-cyan-500',
    textColor: 'text-cyan-400',
    label: 'EP Zone LONG',
    accuracy: '65-87%',
    boost: '+20',
  },
  EP_ZONE_SHORT: {
    icon: Target,
    color: 'bg-pink-500',
    textColor: 'text-pink-400',
    label: 'EP Zone SHORT',
    accuracy: '65-87%',
    boost: '+20',
  },
  DHOL_DAY_HIGH: {
    icon: TrendingUp,
    color: 'bg-lime-500',
    textColor: 'text-lime-400',
    label: 'DHOL: Day HIGH',
    accuracy: '69.8%',
    boost: '+12',
  },
  DHOL_DAY_LOW: {
    icon: TrendingDown,
    color: 'bg-fuchsia-500',
    textColor: 'text-fuchsia-400',
    label: 'DHOL: Day LOW',
    accuracy: '69.8%',
    boost: '+12',
  },
  SLINGSHOT_BULLISH: {
    icon: Activity,
    color: 'bg-sky-500',
    textColor: 'text-sky-400',
    label: 'Slingshot Bullish',
    accuracy: '65%',
    boost: '+8',
  },
  SLINGSHOT_BEARISH: {
    icon: Activity,
    color: 'bg-violet-500',
    textColor: 'text-violet-400',
    label: 'Slingshot Bearish',
    accuracy: '65%',
    boost: '+8',
  },
  AUCTION_FAILURE_LONG: {
    icon: RefreshCw,
    color: 'bg-emerald-600',
    textColor: 'text-emerald-300',
    label: 'Auction Fail LONG',
    accuracy: '70%',
    boost: '+15',
  },
  AUCTION_FAILURE_SHORT: {
    icon: RefreshCw,
    color: 'bg-red-600',
    textColor: 'text-red-300',
    label: 'Auction Fail SHORT',
    accuracy: '70%',
    boost: '+15',
  },
  // Phase 3 Context Signals
  DAY_TYPE_TREND: {
    icon: TrendingUp,
    color: 'bg-blue-600',
    textColor: 'text-blue-400',
    label: 'Trend Day',
    accuracy: 'Context',
    boost: '+8',
    isContext: true,
  },
  DAY_TYPE_NORMAL: {
    icon: BarChart2,
    color: 'bg-gray-600',
    textColor: 'text-gray-400',
    label: 'Normal Day',
    accuracy: 'Context',
    boost: '+2',
    isContext: true,
  },
  DAY_TYPE_NEUTRAL: {
    icon: Activity,
    color: 'bg-yellow-600',
    textColor: 'text-yellow-400',
    label: 'Neutral Day',
    accuracy: 'Context',
    boost: '+3',
    isContext: true,
  },
  DAY_TYPE_NON_TREND: {
    icon: AlertTriangle,
    color: 'bg-slate-600',
    textColor: 'text-slate-400',
    label: 'Non-Trend Day',
    accuracy: 'Context',
    boost: '+1',
    isContext: true,
  },
  PROFILE_P_SHAPE: {
    icon: TrendingUp,
    color: 'bg-green-600',
    textColor: 'text-green-400',
    label: 'P-Shape Profile',
    accuracy: 'Context',
    boost: '+5 LONG',
    isContext: true,
  },
  PROFILE_B_SHAPE: {
    icon: TrendingDown,
    color: 'bg-red-600',
    textColor: 'text-red-400',
    label: 'b-Shape Profile',
    accuracy: 'Context',
    boost: '+5 SHORT',
    isContext: true,
  },
  PROFILE_D_SHAPE: {
    icon: BarChart2,
    color: 'bg-gray-500',
    textColor: 'text-gray-400',
    label: 'D-Shape Profile',
    accuracy: 'Context',
    boost: '+1',
    isContext: true,
  },
  OPEN_DRIVE: {
    icon: TrendingUp,
    color: 'bg-indigo-600',
    textColor: 'text-indigo-400',
    label: 'Open Drive',
    accuracy: 'Context',
    boost: '+10',
    isContext: true,
  },
  OPEN_TEST_DRIVE: {
    icon: Activity,
    color: 'bg-purple-600',
    textColor: 'text-purple-400',
    label: 'Open Test Drive',
    accuracy: 'Context',
    boost: '+6',
    isContext: true,
  },
  OPEN_REJECTION_REVERSE: {
    icon: RefreshCw,
    color: 'bg-amber-600',
    textColor: 'text-amber-400',
    label: 'Open Rejection',
    accuracy: 'Context',
    boost: '+5',
    isContext: true,
  },
  OPEN_AUCTION: {
    icon: BarChart2,
    color: 'bg-gray-600',
    textColor: 'text-gray-400',
    label: 'Open Auction',
    accuracy: 'Context',
    boost: '+2',
    isContext: true,
  },
  // Phase 4 Signals
  LPZ_ENTRY: {
    icon: Activity,
    color: 'bg-violet-600',
    textColor: 'text-violet-400',
    label: 'In LPZ Zone',
    accuracy: 'Alert',
    boost: '+3',
  },
  IB_EXTENSION_1_5X: {
    icon: TrendingUp,
    color: 'bg-cyan-600',
    textColor: 'text-cyan-400',
    label: 'IB Ext 1.5x',
    accuracy: 'Trend',
    boost: '+5',
  },
  IB_EXTENSION_2X: {
    icon: TrendingUp,
    color: 'bg-cyan-500',
    textColor: 'text-cyan-300',
    label: 'IB Ext 2x',
    accuracy: 'Strong',
    boost: '+10',
  },
  SPIKE_DELTA_BULLISH: {
    icon: TrendingUp,
    color: 'bg-green-500',
    textColor: 'text-green-400',
    label: 'Spike Bullish',
    accuracy: 'Momentum',
    boost: '+6',
  },
  SPIKE_DELTA_BEARISH: {
    icon: TrendingDown,
    color: 'bg-red-500',
    textColor: 'text-red-400',
    label: 'Spike Bearish',
    accuracy: 'Momentum',
    boost: '+6',
  },
  EXCESS_TAIL_SUPPORT: {
    icon: Target,
    color: 'bg-green-600',
    textColor: 'text-green-400',
    label: 'Excess Support',
    accuracy: 'S/R',
    boost: '+4',
  },
  EXCESS_TAIL_RESISTANCE: {
    icon: Target,
    color: 'bg-red-600',
    textColor: 'text-red-400',
    label: 'Excess Resistance',
    accuracy: 'S/R',
    boost: '+4',
  },
};

// Strength to badge variant
const STRENGTH_VARIANT = {
  LOW: 'outline',
  MEDIUM: 'secondary',
  HIGH: 'default',
  EXTREME: 'destructive',
};

/**
 * Single Signal Card Component
 */
const SignalCard = ({ signal }) => {
  const config = SIGNAL_CONFIG[signal.signal_type] || {
    icon: Target,
    color: 'bg-gray-500',
    textColor: 'text-gray-400',
    label: signal.signal_type,
    accuracy: 'N/A',
  };
  
  const Icon = config.icon;
  const isLong = signal.signal_type.includes('LONG') || 
                 signal.signal_type.includes('BULLISH') || 
                 signal.signal_type === 'INVENTORY_TOO_SHORT';
  
  return (
    <div 
      data-testid={`advanced-signal-${signal.signal_type}`}
      className={`p-3 rounded-lg border ${isLong ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-red-500/30 bg-red-500/5'}`}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <div className={`p-1.5 rounded ${config.color}`}>
            <Icon className="w-4 h-4 text-white" />
          </div>
          <span className={`font-semibold ${config.textColor}`}>
            {config.label}
          </span>
          {config.boost && (
            <span 
              data-testid={`boost-badge-${signal.signal_type}`}
              className="text-xs font-mono px-1.5 py-0.5 rounded bg-yellow-500/20 text-yellow-400 border border-yellow-500/30"
            >
              {config.boost}pts
            </span>
          )}
        </div>
        <Badge variant={STRENGTH_VARIANT[signal.strength] || 'outline'}>
          {signal.confidence_pct?.toFixed(0)}%
        </Badge>
      </div>
      
      {/* Price Levels */}
      {(signal.entry_price || signal.stop_loss || signal.target_1) && (
        <div className="grid grid-cols-3 gap-2 text-xs mb-2">
          {signal.entry_price && (
            <div className="bg-black/20 rounded p-1.5">
              <div className="text-gray-500">Entry</div>
              <div className="font-mono text-white">{signal.entry_price?.toFixed(2)}</div>
            </div>
          )}
          {signal.stop_loss && (
            <div className="bg-black/20 rounded p-1.5">
              <div className="text-gray-500">Stop</div>
              <div className="font-mono text-red-400">{signal.stop_loss?.toFixed(2)}</div>
            </div>
          )}
          {signal.target_1 && (
            <div className="bg-black/20 rounded p-1.5">
              <div className="text-gray-500">Target</div>
              <div className="font-mono text-green-400">{signal.target_1?.toFixed(2)}</div>
            </div>
          )}
        </div>
      )}
      
      {/* Supporting Factors */}
      {signal.supporting_factors?.length > 0 && (
        <div className="text-xs text-gray-400 space-y-0.5">
          {signal.supporting_factors.slice(0, 2).map((factor, i) => (
            <div key={i} className="flex items-center gap-1">
              <ChevronRight className="w-3 h-3" />
              <span>{factor}</span>
            </div>
          ))}
        </div>
      )}
      
      {/* Accuracy Badge */}
      <div className="mt-2 flex items-center justify-between text-xs">
        <span className="text-gray-500">Backtested: {config.accuracy}</span>
        {signal.expires_at && (
          <span className="text-gray-600">
            Expires: {new Date(signal.expires_at).toLocaleTimeString()}
          </span>
        )}
      </div>
    </div>
  );
};

/**
 * Inventory State Card Component
 */
const InventoryCard = ({ inventory, symbol }) => {
  const hasBias = inventory?.inventory_bias;
  const isTooShort = inventory?.inventory_bias === 'TOO_SHORT';
  const isTooLong = inventory?.inventory_bias === 'TOO_LONG';
  
  return (
    <div 
      data-testid={`inventory-state-${symbol}`}
      className={`p-3 rounded-lg border ${
        hasBias 
          ? (isTooShort ? 'border-amber-500/50 bg-amber-500/10' : 'border-purple-500/50 bg-purple-500/10')
          : 'border-gray-700 bg-gray-800/50'
      }`}
    >
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium text-gray-300">Inventory State</span>
        {hasBias && (
          <Badge variant={isTooShort ? 'warning' : 'destructive'}>
            {inventory.inventory_bias}
          </Badge>
        )}
      </div>
      
      <div className="grid grid-cols-2 gap-3 text-xs">
        <div>
          <div className="text-gray-500">Consecutive Poor Highs</div>
          <div className={`font-mono text-lg ${inventory?.consecutive_poor_highs >= 2 ? 'text-purple-400' : 'text-gray-400'}`}>
            {inventory?.consecutive_poor_highs || 0}
          </div>
        </div>
        <div>
          <div className="text-gray-500">Consecutive Poor Lows</div>
          <div className={`font-mono text-lg ${inventory?.consecutive_poor_lows >= 2 ? 'text-amber-400' : 'text-gray-400'}`}>
            {inventory?.consecutive_poor_lows || 0}
          </div>
        </div>
      </div>
      
      {hasBias && (
        <div className="mt-2 text-xs text-gray-400">
          {isTooShort 
            ? 'Sellers exhausted - any dip is buying opportunity'
            : 'Buyers exhausted - any rally is selling opportunity'
          }
        </div>
      )}
    </div>
  );
};

/**
 * Market Context Card Component (Phase 3)
 */
const MarketContextCard = ({ dayType, profileShape, openAuction }) => {
  // Always show the context card - it shows "Pending" for null values
  
  const getDayTypeColor = (type) => {
    switch (type) {
      case 'TREND': return 'text-blue-400 bg-blue-500/10 border-blue-500/30';
      case 'NORMAL': return 'text-gray-400 bg-gray-500/10 border-gray-500/30';
      case 'NEUTRAL': return 'text-yellow-400 bg-yellow-500/10 border-yellow-500/30';
      case 'NON_TREND': return 'text-slate-400 bg-slate-500/10 border-slate-500/30';
      default: return 'text-gray-500 bg-gray-500/5 border-gray-700';
    }
  };
  
  const getShapeColor = (shape) => {
    switch (shape) {
      case 'P': return 'text-green-400';
      case 'B': return 'text-red-400';
      case 'D': return 'text-gray-400';
      default: return 'text-gray-500';
    }
  };
  
  const getOpenTypeColor = (type) => {
    switch (type) {
      case 'DRIVE': return 'text-indigo-400';
      case 'TEST_DRIVE': return 'text-purple-400';
      case 'REJECTION_REVERSE': return 'text-amber-400';
      case 'AUCTION': return 'text-gray-400';
      default: return 'text-gray-500';
    }
  };

  return (
    <div 
      data-testid="market-context-card"
      className="p-3 rounded-lg border border-indigo-500/30 bg-indigo-500/5"
    >
      <div className="flex items-center gap-2 mb-2">
        <BarChart2 className="w-4 h-4 text-indigo-400" />
        <span className="text-sm font-medium text-gray-300">Market Context</span>
      </div>
      
      <div className="grid grid-cols-3 gap-2 text-xs">
        {/* Day Type */}
        <div className={`p-2 rounded border ${getDayTypeColor(dayType?.day_type)}`}>
          <div className="text-gray-500 mb-1">Day Type</div>
          <div className="font-semibold">
            {dayType?.day_type || 'Pending'}
          </div>
          {dayType?.extended_above && !dayType?.extended_below && (
            <div className="text-xs mt-1">Ext UP</div>
          )}
          {dayType?.extended_below && !dayType?.extended_above && (
            <div className="text-xs mt-1">Ext DOWN</div>
          )}
        </div>
        
        {/* Profile Shape */}
        <div className="p-2 rounded border border-gray-700 bg-gray-800/50">
          <div className="text-gray-500 mb-1">Profile</div>
          <div className={`font-semibold ${getShapeColor(profileShape?.shape)}`}>
            {profileShape?.shape ? `${profileShape.shape}-Shape` : 'Pending'}
          </div>
          {profileShape?.volume_skew !== 0 && (
            <div className="text-xs mt-1 text-gray-500">
              Skew: {(profileShape.volume_skew * 100).toFixed(0)}%
            </div>
          )}
        </div>
        
        {/* Open Auction */}
        <div className="p-2 rounded border border-gray-700 bg-gray-800/50">
          <div className="text-gray-500 mb-1">Open Type</div>
          <div className={`font-semibold ${getOpenTypeColor(openAuction?.open_type)}`}>
            {openAuction?.open_type || 'Pending'}
          </div>
          {openAuction?.open_direction && (
            <div className={`text-xs mt-1 ${openAuction.open_direction === 'UP' ? 'text-green-400' : 'text-red-400'}`}>
              {openAuction.open_direction}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * Main Advanced Signals Panel
 */
const AdvancedSignalsPanel = ({ symbol = 'NIFTY', wsMessage = null }) => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  
  // Fetch summary from API
  const fetchSummary = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${API_URL}/api/advanced-signals/summary/${symbol}`);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const data = await response.json();
      setSummary(data);
      setLastUpdate(new Date());
    } catch (err) {
      setError(err.message);
      console.error('Failed to fetch advanced signals:', err);
    } finally {
      setLoading(false);
    }
  }, [symbol]);
  
  // Initial fetch and periodic refresh
  useEffect(() => {
    fetchSummary();
    
    // Refresh every 30 seconds
    const interval = setInterval(fetchSummary, 30000);
    return () => clearInterval(interval);
  }, [fetchSummary]);
  
  // Handle WebSocket messages for real-time updates
  useEffect(() => {
    if (wsMessage?.type === 'ADVANCED_SIGNAL' && wsMessage?.data?.symbol === symbol) {
      // Prepend new signal to the list
      setSummary(prev => {
        if (!prev) return prev;
        
        const existingSignals = prev.signals || [];
        const newSignal = wsMessage.data;
        
        // Check if signal already exists (by type and trigger price)
        const exists = existingSignals.some(
          s => s.signal_type === newSignal.signal_type && 
               Math.abs(s.trigger_price - newSignal.trigger_price) < 1
        );
        
        if (exists) return prev;
        
        return {
          ...prev,
          signals: [newSignal, ...existingSignals].slice(0, 10),
          active_signal_count: prev.active_signal_count + 1,
        };
      });
      setLastUpdate(new Date());
    }
  }, [wsMessage, symbol]);
  
  const hasSignals = summary?.signals?.length > 0;
  
  return (
    <Card 
      data-testid="advanced-signals-panel"
      className="bg-gray-900 border-gray-800"
    >
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-sm">
            <Target className="w-4 h-4 text-cyan-400" />
            <span>Advanced Signals</span>
            <Badge variant="outline" className="ml-2">Phase 4</Badge>
          </CardTitle>
          <div className="flex items-center gap-2">
            {lastUpdate && (
              <span className="text-xs text-gray-500">
                {lastUpdate.toLocaleTimeString()}
              </span>
            )}
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={fetchSummary}
              disabled={loading}
              data-testid="refresh-advanced-signals"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-3">
        {error && (
          <div className="text-sm text-red-400 bg-red-500/10 rounded p-2">
            Error: {error}
          </div>
        )}
        
        {/* Phase 3: Market Context */}
        {summary && (
          <MarketContextCard 
            dayType={summary.day_type}
            profileShape={summary.profile_shape}
            openAuction={summary.open_auction}
          />
        )}
        
        {/* Inventory State */}
        {summary?.inventory_state && (
          <InventoryCard 
            inventory={summary.inventory_state} 
            symbol={symbol} 
          />
        )}
        
        {/* Active Signals */}
        {hasSignals ? (
          <div className="space-y-2">
            <div className="text-xs text-gray-500 uppercase tracking-wider">
              Active Signals ({summary.active_signal_count})
            </div>
            {summary.signals.map((signal, index) => (
              <SignalCard key={`${signal.signal_type}-${index}`} signal={signal} />
            ))}
          </div>
        ) : (
          <div className="text-center py-4 text-gray-500 text-sm">
            No active signals for {symbol}
          </div>
        )}
        
        {/* Ledge Stats */}
        {summary && (
          <div className="flex items-center justify-between text-xs text-gray-500 pt-2 border-t border-gray-800">
            <span>Active Ledges: {summary.ledge_count || 0}</span>
            <span>Pierce Events: {summary.pierce_count || 0}</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AdvancedSignalsPanel;
